import React, { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import StaticPageComponent from '@fuww/library/src/StaticPage';
import { NextPageContext } from 'next';
import messages from '../lib/messages.json';
import Head from '../components/Head';
import HomeLink from '../components/HomeLink';
import Markdown from '../components/Markdown';
import throw404 from '../lib/throw404';
import Loader from '../components/Loader';
import correctLocalizedRoute from '../lib/correctLocalizedRoute';
import useMarkdownLoader from '../lib/useMarkdownLoader';
import Hero from '../components/Hero';
import Layout from '../components/Layout';

const headerImageUrls = {
  mobile: [
    'https://r.fashionunited.com/Xx2nyZd_DEAJ5SNhFZ2YlJRQ0wchDMGQKw5v3hWD-SQ/resize:fill:42:34:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcG9zaXRpb25zX2luX2Zhc2hpb25fcGFnZV9iYW5uZXJfbW9iaWxlLmpwZw',
    'https://r.fashionunited.com/iD_0eGqFpjf7mcxFUhzEVb8iQWgvgxkeFy5LA7xb2_Q/resize:fill:360:288:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcG9zaXRpb25zX2luX2Zhc2hpb25fcGFnZV9iYW5uZXJfbW9iaWxlLmpwZw',
    'https://r.fashionunited.com/Hu7J3fZliCrCDIcfbbf9aky1V_sNXuuNgK6BMwdLw0Y/resize:fill:720:576:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcG9zaXRpb25zX2luX2Zhc2hpb25fcGFnZV9iYW5uZXJfbW9iaWxlLmpwZw',
  ],
  desktop: [
    'https://r.fashionunited.com/V8naScSRu9uRv3VidNWlFG2GIoa8M085uZPjFsSLC8M/resize:fill:42:8:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcG9zaXRpb25zX2luX2Zhc2hpb25fcGFnZV9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/0aH8Xim5_bfjBfCTJxaLkxeO2Y853ZCe9A85kUNGUH8/resize:fill:1350:277:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcG9zaXRpb25zX2luX2Zhc2hpb25fcGFnZV9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/_L2bhkdpabwFYDALtTEdeWJ_TUopWhC7R_x7-X_w41U/resize:fill:2560:526:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcG9zaXRpb25zX2luX2Zhc2hpb25fcGFnZV9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/oCzyjNaUAOcG07lYB3DJS6qqW-2HW5Nh4FU7T8yAQCo/resize:fill:5120:1052:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcG9zaXRpb25zX2luX2Zhc2hpb25fcGFnZV9iYW5uZXJfZGVza3RvcC5qcGc',
  ],
};

const markdownImports = {
  'nl-NL': () => import('../data/staticPages/positions-in-fashion/nl.md'),
  'en-GB': () => import('../data/staticPages/positions-in-fashion/en.md'),
  'en-US': () => import('../data/staticPages/positions-in-fashion/en.md'),
  'de-DE': () => import('../data/staticPages/positions-in-fashion/de.md'),
  'es-ES': () => import('../data/staticPages/positions-in-fashion/es.md'),
} as const;

const PositionsInFashionPage = () => {
  const intl = useIntl();
  const { markdown, loading } = useMarkdownLoader(markdownImports);

  const breadcrumbs = [
    <HomeLink />,
    intl.formatMessage(messages['positions-in-fashion.title']),
  ];

  if (loading) return <Loader fullScreen />;

  if (!markdown) return throw404();

  return (
    <>
      <Head
        title={intl.formatMessage(
          messages['positions-in-fashion.title'],
        )}
        description={intl.formatMessage(
          messages['positions-in-fashion.description'],
        )}
      />
      <StaticPageComponent
        breadcrumbs={breadcrumbs}
        title={intl.formatMessage(
          messages['positions-in-fashion.title'],
        )}
        content={(
          <Markdown markdown={markdown} allowGfm allowHtml />
        )}
      />
    </>
  );
};

PositionsInFashionPage.getLayout = (page: ReactElement) => (
  <Layout
    header={(
      <Hero
        imageUrls={headerImageUrls}
        alt="Positions in fashion banner image"
      />
    )}
  >
    {page}
  </Layout>
);

PositionsInFashionPage.getInitialProps = (context: NextPageContext) => {
  correctLocalizedRoute(context, 'positions-in-fashion');
  return {};
};

export default PositionsInFashionPage;
